/**
 * PostInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { PostAttachment } from './post_attachment'
import type { UserBriefInfo } from './user_brief_info'

/**
 * PostInfo
 */
export interface PostInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 禁用状态 */
  disabled?: boolean
  /** 禁用时间 */
  disabled_at?: string
  /** 禁用截止时间（null表示永久禁用） */
  disabled_until?: string
  /** 禁用原因 */
  disabled_reason?: string
  /** 帖子类型 */
  type?: string
  /** 帖子标题 */
  title?: string
  /** 帖子文本内容 */
  text?: string
  /** 发布者ID */
  author_id?: string
  /** 发布者信息快照（username、nickname、avatar、bio、段位、等级、星数等） */
  author_snapshot?: UserBriefInfo
  /** 创建管理员ID（后台发帖） */
  creator_staff_id?: string
  /** 富媒体附件列表 */
  attachments?: PostAttachment[]
  /** 附件数量 */
  attachment_count?: number
  /** 视频时长（秒） */
  file_duration?: number
  /** 文件大小（字节） */
  file_size?: number
  /** 帖子可见范围 */
  visibility?: string
}

/**
 * PostInfo Schema
 */
export const PostInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    disabled: {
      description: '禁用状态',
      format: 'bool',
      type: 'boolean',
      label: '禁用状态',
    },
    disabled_at: {
      description: '禁用时间',
      format: '*time.Time',
      type: 'string',
      label: '禁用时间',
    },
    disabled_until: {
      description: '禁用截止时间（null表示永久禁用）',
      format: '*time.Time',
      type: 'string',
      label: '禁用截止时间',
    },
    disabled_reason: {
      description: '禁用原因',
      format: 'string',
      type: 'string',
      label: '禁用原因',
    },
    type: {
      description: '帖子类型',
      format: 'cons.PostType',
      type: 'string',
      label: '帖子类型',
    },
    title: {
      description: '帖子标题',
      format: 'string',
      type: 'string',
      label: '帖子标题',
    },
    text: {
      description: '帖子文本内容',
      format: 'string',
      type: 'string',
      label: '帖子文本内容',
    },
    author_id: {
      description: '发布者ID',
      format: 'string',
      type: 'string',
      label: '发布者ID',
    },
    author_snapshot: {
      $ref: 'UserBriefInfo',
      description: '发布者信息快照（username、nickname、avatar、bio、段位、等级、星数等）',
      label: '发布者信息快照',
    },
    creator_staff_id: {
      description: '创建管理员ID（后台发帖）',
      format: 'string',
      type: 'string',
      label: '创建管理员ID',
    },
    attachments: {
      description: '富媒体附件列表',
      format: '[]entity.PostAttachment',
      items: { $ref: 'PostAttachment', description: '' },
      type: 'array',
      label: '富媒体附件列表',
    },
    attachment_count: {
      description: '附件数量',
      format: 'int',
      type: 'integer',
      label: '附件数量',
    },
    file_duration: {
      description: '视频时长（秒）',
      format: 'int',
      type: 'integer',
      label: '视频时长',
    },
    file_size: {
      description: '文件大小（字节）',
      format: 'int',
      type: 'integer',
      label: '文件大小',
    },
    visibility: {
      description: '帖子可见范围',
      format: 'cons.PostVisibility',
      type: 'string',
      label: '帖子可见范围',
    },
  },
  type: 'object',
}
